// Breakpoint generation — PSD §4.2.2 (F2.2, F2.4). Engine layer (framework-free).
import { BREAKPOINT_MERGE_THRESHOLD_USD } from './constants';
/** Total liquidation preference value of a preferred instrument. */
export function preferenceValue(p) {
    if (p.kind !== 'preferred')
        return 0;
    const mult = p.liquidationMultiple ?? 0;
    return Math.max(0, mult) * Math.max(0, p.issuePrice) * Math.max(0, p.sharesOutstanding);
}
/** Effective conversion ratio (defaults to 1.0 if missing — emits warning upstream). */
export function effectiveConversionRatio(p) {
    return p.conversionRatio && p.conversionRatio > 0 ? p.conversionRatio : 1;
}
/** As-converted (common-equivalent) share count for an instrument. */
export function asConvertedShares(inst) {
    switch (inst.kind) {
        case 'common':
        case 'option':
        case 'warrant':
        case 'rsu':
            return Math.max(0, inst.sharesOutstanding);
        case 'preferred':
            return Math.max(0, inst.sharesOutstanding) * effectiveConversionRatio(inst);
        case 'pool':
            return Math.max(0, inst.sharesAuthorized - inst.sharesOutstanding);
        default:
            return 0;
    }
}
/** Sum of as-converted shares across all instruments. */
export function totalAsConvertedShares(cap) {
    return cap.instruments.reduce((s, i) => s + asConvertedShares(i), 0);
}
/** Preferred instruments ordered by seniority asc then issueDate asc. */
export function preferredOrdered(cap) {
    return cap.instruments
        .filter((i) => i.kind === 'preferred')
        .sort((a, b) => {
        /* v8 ignore next */
        const sa = a.seniority ?? 1;
        /* v8 ignore next */
        const sb = b.seniority ?? 1;
        if (sa !== sb)
            return sa - sb;
        /* v8 ignore next */
        return (a.issueDate ?? '').localeCompare(b.issueDate ?? '');
    });
}
/**
 * Generate the ordered breakpoint schedule (PSD §4.2.2).
 * Each breakpoint owns the tranche [its strike, next strike].
 */
export function generateBreakpoints(cap) {
    const issues = [];
    const raw = [];
    const preferred = preferredOrdered(cap);
    // Cumulative preference threshold; preferred are paid in seniority order.
    let cumulative = 0;
    // Origin breakpoint at 0.
    if (preferred.length > 0) {
        raw.push({ strike: 0, cause: 'origin', classesSharing: [] });
    }
    else {
        // No preferred: all value flows to common-equivalent classes.
        const owners = commonEquivalentIds(cap);
        raw.push({ strike: 0, cause: 'origin', classesSharing: owners });
    }
    const fdShares = totalAsConvertedShares(cap);
    for (const p of preferred) {
        if (!p.conversionRatio || p.conversionRatio <= 0) {
            issues.push({
                code: 'MISSING_CONVERSION_RATIO',
                message: `Preferred "${p.name}" has no conversion ratio; assuming 1.0.`,
                instrumentId: p.id,
            });
        }
        const prefVal = preferenceValue(p);
        if (prefVal <= 0) {
            // No preference: preferred converts immediately, shares pro-rata.
            issues.push({
                code: 'MISSING_PREFERENCE_TERMS',
                message: `Preferred "${p.name}" has no liquidation preference terms.`,
                instrumentId: p.id,
            });
            continue;
        }
        // Preference interval: [cumulative, cumulative + prefVal] owned by this preferred.
        const prefStart = cumulative;
        const prefEnd = cumulative + prefVal;
        setOwnersAtOrInsert(raw, prefStart, [p.id], `preference:${p.name}`);
        cumulative = prefEnd;
        // Crossover: where preferred is indifferent between preference and as-converted.
        // For non-participating: preferred takes MAX(preference, as-converted share).
        // Crossover V_c solves: prefVal = ratio*shares_p / fdShares * (V_c - cumulativeAtAllPrefs)
        // For simplicity (standard OPM), the preference breakpoint marks the transition to
        // pro-rata sharing once all senior preferences are satisfied.
        const participation = p.participation ?? 'nonParticipating';
        if (participation === 'nonParticipating') {
            // After its preference is met, this preferred converts and shares pro-rata
            // with common-equivalent classes above the cumulative threshold.
            const owners = commonEquivalentIds(cap).concat(p.id);
            setOwnersAtOrInsert(raw, cumulative, owners, `as-converted:${p.name}`);
        }
        else if (participation === 'participating' || participation === 'cappedParticipating') {
            // Participating: preferred gets preference then shares in upside until cap.
            if (participation === 'cappedParticipating' && p.participationCap) {
                const capValue = Math.max(0, p.participationCap) * Math.max(0, p.issuePrice) * Math.max(0, p.sharesOutstanding);
                const capStrike = cumulative + (capValue - prefVal);
                if (capStrike > cumulative && capStrike > 0) {
                    setOwnersAtOrInsert(raw, capStrike, commonEquivalentIds(cap).concat(p.id), `participation-cap:${p.name}`);
                }
            }
            // Above preferences (and cap if any), preferred participates with common pro-rata.
            const owners = commonEquivalentIds(cap).concat(p.id);
            setOwnersAtOrInsert(raw, cumulative, owners, `participation:${p.name}`);
        }
        // suppress unused fdShares in some paths
        void fdShares;
    }
    // If no preferred introduced an as-converted region, ensure common owns the tail.
    if (preferred.length === 0 || raw.every((r) => r.classesSharing.length === 0)) {
        setOwnersAtOrInsert(raw, 0, commonEquivalentIds(cap), 'origin');
    }
    // Sort, merge near-duplicates, clamp negatives, renumber.
    const sorted = raw
        .filter((r) => Number.isFinite(r.strike))
        .map((r) => {
        /* v8 ignore start */
        if (r.strike < 0) {
            issues.push({
                code: 'NEGATIVE_BREAKPOINT',
                message: `Breakpoint strike ${r.strike} was negative; clamped to 0.`,
                instrumentId: null,
            });
            return { ...r, strike: 0 };
        }
        /* v8 ignore stop */
        return { ...r };
    })
        .sort((a, b) => a.strike - b.strike);
    const merged = mergeBreakpoints(sorted);
    const breakpoints = merged.map((r, i) => ({
        index: i,
        equityValueLow: r.strike,
        equityValueHigh: r.strike,
        cause: r.cause,
        classesSharing: r.classesSharing,
    }));
    /* v8 ignore start */
    if (breakpoints.length === 0) {
        breakpoints.push({
            index: 0,
            equityValueLow: 0,
            equityValueHigh: 0,
            cause: 'origin',
            classesSharing: commonEquivalentIds(cap),
        });
    }
    /* v8 ignore stop */
    // Detect empty owners
    for (const bp of breakpoints) {
        if (bp.classesSharing.length === 0) {
            issues.push({
                code: 'EMPTY_TRANCHE_OWNERS',
                message: `Breakpoint ${bp.index} (${bp.cause}) has no owning classes.`,
                instrumentId: null,
            });
        }
    }
    return { breakpoints, issues };
}
function commonEquivalentIds(cap) {
    return cap.instruments
        .filter((i) => i.kind === 'common' || i.kind === 'option' || i.kind === 'warrant' || i.kind === 'rsu' || i.kind === 'pool')
        .map((i) => i.id);
}
/** Set owners for the interval starting at `strike`; insert a breakpoint if absent. */
function setOwnersAtOrInsert(raw, strike, owners, cause) {
    const existing = raw.find((r) => Math.abs(r.strike - strike) < 1e-9);
    if (existing) {
        existing.classesSharing = owners;
        existing.cause = cause;
    }
    else {
        raw.push({ strike, classesSharing: owners, cause });
    }
}
/** Merge breakpoints within the merge threshold; union their owners. */
function mergeBreakpoints(sorted) {
    /* v8 ignore next */
    if (sorted.length === 0)
        return [];
    const first = sorted[0];
    const out = [first ? { ...first } : /* v8 ignore next */ { strike: 0, cause: 'origin', classesSharing: [] }];
    for (let i = 1; i < sorted.length; i++) {
        const last = out[out.length - 1];
        const cur = sorted[i];
        /* v8 ignore next */
        if (!last || !cur)
            continue;
        /* v8 ignore start */
        if (cur.strike === 0 && last.strike === 0) {
            // collapse duplicate origins
            last.classesSharing = union(last.classesSharing, cur.classesSharing);
            continue;
        }
        /* v8 ignore stop */
        if (cur.strike - last.strike <= BREAKPOINT_MERGE_THRESHOLD_USD) {
            last.classesSharing = union(last.classesSharing, cur.classesSharing);
        }
        else {
            out.push({ ...cur });
        }
    }
    return out;
}
function union(a, b) {
    return Array.from(new Set([...a, ...b]));
}
//# sourceMappingURL=breakpoints.js.map